import React, { useState } from "react";
import Icon from "./Icon";
import GallerySectionHeading from "./GallerySectionHeading";
import GalleryLightbox from "./GalleryLightbox";
import useLightbox from "../hooks/useLightbox";
import "../styles/GallerySections.css";
import "../styles/FeaturedListGallerySection.css";

/*
 * Layout 4 — Orbit Showcase.
 * One large centered piece with the rest of the set arranged as
 * satellites on a ring around it — clicking a satellite swaps the
 * centerpiece, clicking the centerpiece opens the full lightbox.
 * Collapses to the earlier scrollable thumbnail row on narrow screens,
 * where a literal orbit has no room to breathe.
 */
export default function FeaturedListGallerySection({ items, eyebrow, heading, sub }) {
  const [featuredIndex, setFeaturedIndex] = useState(0);
  const { activeIndex, activeItem, open, close, showPrev, showNext } = useLightbox(items);

  const featured = items[featuredIndex] ?? items[0];
  const satellites = items.filter((_, index) => index !== featuredIndex);

  return (
    <section className="tp-section tp-gallery">
      <div className="tp-container">
        <GallerySectionHeading eyebrow={eyebrow} heading={heading} sub={sub} />

        <div className="tp-orbit">
          <div className="tp-orbit__stage">
            <span className="tp-orbit__ring" aria-hidden="true" />
            <button
              type="button"
              className="tp-orbit__center"
              onClick={() => open(featuredIndex)}
              aria-label={`View ${featured.title} full size`}
            >
              <img src={featured.image} alt={featured.title} />
              <span className="tp-orbit__center-caption">
                <span>{featured.title}</span>
                <span className="tp-orbit__zoom">
                  <Icon name="search" size={16} />
                </span>
              </span>
            </button>

            {items.map((item, index) => {
              if (index === featuredIndex) return null;
              const satelliteIndex = satellites.indexOf(item);
              return (
                <button
                  type="button"
                  key={item.id}
                  className="tp-orbit__satellite"
                  style={{
                    "--i": satelliteIndex,
                    "--total": satellites.length,
                  }}
                  onClick={() => setFeaturedIndex(index)}
                  aria-label={`Show ${item.title} as the centerpiece`}
                >
                  <img src={item.image} alt={item.title} loading="lazy" />
                </button>
              );
            })}
          </div>

          {/* Same items, shown as a plain scrollable row — this is
              what actually renders on narrow screens (see CSS);
              display:none on the inactive variant keeps it out of
              the tab order and the accessibility tree automatically. */}
          <div className="tp-orbit__list">
            {items.map((item, index) => (
              <button
                type="button"
                key={item.id}
                className={`tp-featured__thumb${
                  index === featuredIndex ? " tp-featured__thumb--active" : ""
                }`}
                onClick={() => setFeaturedIndex(index)}
                aria-label={`Preview ${item.title}`}
                aria-pressed={index === featuredIndex}
              >
                <img src={item.image} alt={item.title} loading="lazy" />
                <span className="tp-featured__thumb-title">{item.title}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <GalleryLightbox
        items={items}
        activeIndex={activeIndex}
        activeItem={activeItem}
        onClose={close}
        onPrev={showPrev}
        onNext={showNext}
      />
    </section>
  );
}
